import { useEffect, useMemo } from 'react';
import { Alert, Stack, Text } from '@mantine/core';
import { IconAlertCircle } from '@tabler/icons-react';
import { Button } from 'src/components';
import { AuthPageLayout } from 'src/auth/components/AuthPageLayout';
import { AuthCard } from 'src/auth/components/AuthCard';
import { usePublicTenantConfig } from 'src/auth/hooks/usePublicTenantConfig';
import { useInviteData } from 'src/auth/hooks/useInviteData';
import { isSsoOnly } from 'src/auth/utils/ory-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { Logger } from 'src/utils/Logger';
import { useSearchParams } from 'react-router';

interface DecodedUrlData {
  tenantId: string;
  token: string;
}

interface EncodedData {
  t: string;
  k: string;
}

const decodeUrlData = (encodedData: string | null): DecodedUrlData | null => {
  if (!encodedData) return null;

  try {
    const decoded = atob(encodedData.replace(/-/g, '+').replace(/_/g, '/'));
    const parsed = JSON.parse(decoded) as EncodedData;
    return {
      tenantId: parsed.t,
      token: parsed.k,
    };
  } catch {
    return null;
  }
};

export const AcceptLinkPage = () => {
  const [searchParams] = useSearchParams();
  const encodedData = searchParams.get('data');

  const urlData = useMemo(() => decodeUrlData(encodedData), [encodedData]);

  const {
    data: inviteData,
    isLoading: isInviteLoading,
    isError: isInviteError,
    error: inviteError,
  } = useInviteData(urlData?.tenantId, urlData?.token);

  const {
    config,
    isLoading: isConfigLoading,
    isError: isConfigError,
    error: configError,
  } = usePublicTenantConfig(urlData?.tenantId);

  useEffect(() => {
    if (isInviteError) {
      Logger.warn('Failed to fetch invite data', {
        tenantId: urlData?.tenantId,
        error: inviteError,
      });
    }
    if (isConfigError) {
      Logger.warn('Failed to fetch tenant config', {
        tenantId: urlData?.tenantId,
        error: configError,
      });
    }
  }, [isInviteError, isConfigError, urlData?.tenantId, inviteError, configError]);

  const handleContinue = () => {
    if (inviteData?.ory_link) {
      window.location.href = inviteData.ory_link;
    }
  };

  if (!urlData) {
    return (
      <AuthPageLayout withHeader>
        <AuthCard>
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            Invalid or missing invite data. Please check your invitation link or contact your
            administrator.
          </Alert>
        </AuthCard>
      </AuthPageLayout>
    );
  }

  const isLoading = isInviteLoading || isConfigLoading;
  const hasError = isInviteError || isConfigError;

  const variant = !isConfigError && config?.authMethod ? config.authMethod : 'password';
  const isSso = isSsoOnly(variant);

  return (
    <AuthPageLayout isLoading={isLoading} withHeader>
      {hasError ? (
        <AuthCard>
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            {isInviteError
              ? 'This invitation link has expired or is invalid. Please contact your administrator for a new invitation.'
              : 'Something went wrong. Please try refreshing the page.'}
          </Alert>
        </AuthCard>
      ) : inviteData ? (
        <Stack align="center" gap="md">
          <Text c="gray.0" fz={36} fw={FONT_WEIGHTS.bold}>
            {inviteData.first_name ? `Welcome, ${inviteData.first_name}!` : 'Welcome!'}
          </Text>
          <Text c="gray.0" ta="center" fz={24} fw={FONT_WEIGHTS.medium}>
            {inviteData.inviter_name
              ? `${inviteData.inviter_name} has invited you to join Afternoon.`
              : 'You have been invited to join Afternoon.'}
            <br />
            {isSso
              ? 'Please sign in with your organization account to continue.'
              : 'Please create your profile to continue.'}
          </Text>
          {inviteData.email && (
            <Text c="dimmed" size="sm">
              {inviteData.email}
            </Text>
          )}

          <Button onClick={handleContinue} size="lg">
            {isSso ? 'Continue with SSO' : 'Accept invite & create profile'}
          </Button>
        </Stack>
      ) : null}
    </AuthPageLayout>
  );
};
